/**
 * Детальные замеры для отладки «Время оценки скриптов» / TBT.
 * Включение: window.__INSALES_COMMON_PERF_DEBUG__ = true до загрузки common,
 * или ?common_perf_debug=1 в URL, или localStorage.insales_common_perf_debug = '1'
 *
 * Помимо console: записи User Timing (performance.mark / performance.measure) —
 * видны в Performance → User Timing / таймлайне (см. MDN Performance#measure).
 *
 * Порядок загрузки модулей (цепочка товара): вызывайте logModuleEval('@/…') в конце файла —
 * в summary будет список с delta от предыдущего модуля (см. logSummary().modules).
 *
 * В консоли: logSummary(), listMeasures(), clearUserTiming()
 */

const PREFIX = '[common-js perf]';
const MEASURE_PREFIX = 'insales:common:';
const NS = '__insalesCommonPerf';

let measureSeq = 0;

function isEnabled() {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.__INSALES_COMMON_PERF_DEBUG__ === true) {
    return true;
  }
  try {
    if (window.localStorage && window.localStorage.getItem('insales_common_perf_debug') === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }
  try {
    const search = typeof window.location !== 'undefined' ? window.location.search : '';
    if (search.indexOf('common_perf_debug=1') !== -1) {
      return true;
    }
  } catch {
    /* ignore */
  }

  return false;
}

function now() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }

  return Date.now();
}

function ensureSummary() {
  if (typeof window === 'undefined') {
    return {};
  }
  if (!window[NS]) {
    window[NS] = {
      marks: [],
      chunks: [],
      modules: [],
      moduleEvalT0: null,
      lastModuleEvalAt: null,
      isOnScreenCalls: 0,
      isOnScreenMs: 0,
    };
  }

  return window[NS];
}

/**
 * Имя mark/measure с общим префиксом (меньше коллизий с другими скриптами).
 * @param {string} name
 * @return {string}
 */
function fullName(name) {
  return MEASURE_PREFIX + name;
}

/**
 * Точка на таймлайне Performance (User Timing mark).
 * @param {string} name — без префикса insales:common:
 */
function mark(name) {
  if (!isEnabled() || typeof performance === 'undefined' || typeof performance.mark !== 'function') {
    return;
  }
  try {
    performance.mark(fullName(name));
  } catch {
    /* ignore quota / duplicate */
  }
}

/**
 * Интервал по двум меткам времени performance.now() (как в MDN measure с measureOptions).
 * @param {string} name — логическое имя измерения
 * @param {number} startTime
 * @param {number} endTime
 */
function measureRange(name, startTime, endTime) {
  if (!isEnabled() || typeof performance === 'undefined' || typeof performance.measure !== 'function') {
    return;
  }
  try {
    performance.measure(fullName(name), {
      start: startTime,
      end: endTime,
    });
  } catch {
    /* ignore */
  }
}

/**
 * measure между двумя именами mark (уже зарегистрированными с тем же префиксом).
 * @param {string} measureName
 * @param {string} startMarkShort
 * @param {string} endMarkShort
 */
function measureBetweenMarks(measureName, startMarkShort, endMarkShort) {
  if (!isEnabled() || typeof performance === 'undefined' || typeof performance.measure !== 'function') {
    return;
  }
  try {
    performance.measure(
      fullName(measureName),
      fullName(startMarkShort),
      fullName(endMarkShort),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Список PerformanceMeasure с префиксом insales:common: (для консоли / отладки).
 * @return {PerformanceMeasure[]}
 */
function listMeasures() {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') {
    return [];
  }
  try {
    return performance.getEntriesByType('measure').filter(function(entry) {
      return entry.name.indexOf(MEASURE_PREFIX) === 0;
    });
  } catch {
    return [];
  }
}

/**
 * Очистить mark/measure User Timing (чтобы не упираться в лимит буфера при повторных прогонах).
 */
function clearUserTiming() {
  if (typeof performance === 'undefined') {
    return;
  }
  try {
    if (typeof performance.clearMarks === 'function') {
      performance.clearMarks();
    }
    if (typeof performance.clearMeasures === 'function') {
      performance.clearMeasures();
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {string} phase
 * @param {Record<string, *>} [detail]
 * @param {number} [durationMs]
 */
function log(phase, detail, durationMs) {
  if (!isEnabled()) {
    return;
  }
  const row = {
    phase,
    ...(detail || {}),
  };
  if (durationMs != null) {
    row.ms = Math.round(durationMs * 100) / 100;
  }
  ensureSummary().marks.push(row);
  const suffix = durationMs != null ? ` ${row.ms}ms` : '';
  const extra = detail && Object.keys(detail).length ? ` ${JSON.stringify(detail)}` : '';
  console.warn(`${PREFIX} ${phase}${suffix}${extra}`);
}

function logSummary() {
  if (!isEnabled()) {
    return;
  }
  const summary = ensureSummary();
  console.warn(`${PREFIX} summary`, {
    marks: summary.marks,
    chunks: summary.chunks,
    modules: summary.modules,
    isOnScreen: {
      calls: summary.isOnScreenCalls,
      ms: Math.round(summary.isOnScreenMs * 100) / 100,
    },
    userTimingMeasures: listMeasures().map(function(entry) {
      return {
        name: entry.name,
        duration: entry.duration,
      };
    }),
  });
}

/**
 * Вызвать в конце модуля (после объявлений). Показывает порядок завершения оценки модуля
 * и интервал от предыдущего такого же вызова (между ними выполнялись другие модули и движок).
 *
 * @param {string} moduleId — например '@/products/index'
 */
function logModuleEval(moduleId) {
  if (!isEnabled()) {
    return;
  }
  const timeNow = now();
  const summary = ensureSummary();
  if (summary.moduleEvalT0 == null) {
    summary.moduleEvalT0 = timeNow;
  }
  const deltaFromPrev = summary.lastModuleEvalAt != null ? timeNow - summary.lastModuleEvalAt : null;
  const sinceFirst = timeNow - summary.moduleEvalT0;
  summary.lastModuleEvalAt = timeNow;
  const row = {
    module: moduleId,
    deltaFromPrevMs: deltaFromPrev != null ? Math.round(deltaFromPrev * 100) / 100 : null,
    sinceFirstModuleEvalMs: Math.round(sinceFirst * 100) / 100,
  };
  summary.modules.push(row);
  mark('module:' + moduleId.replace(/[@./]/g, '_'));
  log('module:eval', row);
}

/**
 * @param {string} name
 * @param {() => T} fn
 * @return {T}
 * @template T
 */
function timeSync(name, fn) {
  if (!isEnabled()) {
    return fn();
  }
  const id = ++measureSeq;
  const unique = `${name}#${id}`;
  const t0 = now();
  try {
    return fn();
  } finally {
    const t1 = now();
    log(name, { id }, t1 - t0);
    measureRange(unique, t0, t1);
  }
}

/**
 * @param {string} name
 * @param {() => void} fn
 */
function timeAsyncEnd(name, fn) {
  if (!isEnabled()) {
    fn();

    return;
  }
  const id = ++measureSeq;
  const unique = `${name}#${id}`;
  const t0 = now();
  try {
    fn();
  } finally {
    const t1 = now();
    log(name, { id }, t1 - t0);
    measureRange(unique, t0, t1);
  }
}

function recordIsOnScreen(durationMs) {
  if (!isEnabled()) {
    return;
  }
  const summary = ensureSummary();
  summary.isOnScreenCalls += 1;
  summary.isOnScreenMs += durationMs;
}

function expose() {
  if (typeof window === 'undefined') {
    return;
  }
  window.logInsalesCommonPerfSummary = logSummary;
  window.listInsalesCommonPerfMeasures = listMeasures;
  window.clearInsalesCommonPerfTiming = clearUserTiming;
}

expose();

export default {
  isEnabled,
  now,
  log,
  logSummary,
  timeSync,
  timeAsyncEnd,
  recordIsOnScreen,
  ensureSummary,
  mark,
  measureRange,
  measureBetweenMarks,
  listMeasures,
  clearUserTiming,
  fullName,
  logModuleEval,
};
